import { apiClient } from "@/lib/api-client";
import type { EventOkthxActionResponse, OkthxEventAction } from "@/types/events.ts";
import { queryOptions, skipToken } from "@tanstack/react-query";

/**
 * What okthx last did to an event, for the event panel on web and native
 * (BIG-9318). Fetched apart from the event because the panel usually serves
 * the event from the calendar-list cache. Keyed under the event, so the
 * socket flush that follows okthx's own writes refreshes it, as does the
 * panel's invalidation after an RSVP. An edit the user makes in the panel
 * carries no okthx mark, so it leaves the record as it was.
 */
export const okthxEventActionQueryOptions = (eventId: string | undefined) =>
  queryOptions({
    queryKey: ["event", eventId, "okthxAction"] as const,
    queryFn: eventId
      ? async ({ signal }) =>
          (
            await apiClient.get<EventOkthxActionResponse>(`/api/a/events/${eventId}/okthx-action`, {
              signal,
            })
          ).data
      : skipToken,
    staleTime: 60_000,
  });

const VERB: Record<OkthxEventAction, string> = {
  created: "scheduled",
  updated: "updated",
  accepted: "accepted",
  tentative: "tentatively accepted",
  declined: "declined",
};

/**
 * "okthx accepted this on Oct 29", dated in the viewer's time zone, with the
 * year when it is not this year.
 */
export function describeOkthxEventAction(
  record: { action: OkthxEventAction; actedAt: string },
  opts: { timeZone?: string; now?: Date } = {},
) {
  const { timeZone, now = new Date() } = opts;
  const actedAt = new Date(record.actedAt);
  const yearOf = (date: Date) =>
    new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone }).format(date);
  const date = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    ...(yearOf(actedAt) === yearOf(now) ? {} : { year: "numeric" }),
    timeZone,
  }).format(actedAt);
  return `okthx ${VERB[record.action]} this on ${date}`;
}
